import { IconMediaMute } from '@doltech/icons-from-dev/IconMediaMute';
import { IconMediaVolume } from '@doltech/icons-from-dev/IconMediaVolume';
import { IconArrowsUndo } from '@doltech/icons/IconArrowsUndo';
import { IconOtherPause } from '@doltech/icons/IconOtherPause';
import { IconOtherPlay } from '@doltech/icons/IconOtherPlay';
import { convertSecondsToMinutedAndSeconds } from '@doltech/utils/lib/date';
import { Slider } from 'antd';
import * as React from 'react';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../../hooks/useDeviceQuery.hook';
import { RequiredAuthFake } from '../../Auth/RequiredAuthFake';
import { Button } from '../../ButtonV2/ButtonV2';
import { Typography } from '../../DesignSystemV2/Typography';
import { AudioPlayerProps, FlatAudioPlayer, FlatPlayerInstance } from './FlatAudioPlayer';
import { ChangeAudioSpeedComponent } from './speed/ChangeAudioSpeedComponent';
import { noop } from 'lodash';

const Main = styled.div`
  display: flex;
  align-items: center;
  .audio-controllers {
    display: flex;
    align-items: center;
    margin-right: 8px;
    button {
      margin-right: 8px;
      &:last-child {
        margin-right: 0;
      }
    }
  }
  .audio-player-container {
    flex: 1;
  }
  .volume-slider {
    display: flex;
    align-items: center;
    .icon-volume {
      margin: 0 8px;
      font-size: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    .__audio-slider {
      width: 80px;
      margin: 0;
    }
  }
  @media ${deviceBreakpoints.mobile} {
    .volume-slider {
      display: none;
    }
  }
`;

interface ReplayableAudioPlayerProps extends AudioPlayerProps {
  url: string;
  showTime?: number;
  vol?: number;
  onVolChange?: (vol) => void;
  requireLogin?: boolean;
  changeSpeed?: boolean;
  playbackRate?: number;
  updatePlaybackSpeed?: (speed) => void;
}

export interface ReplayableAudioRefInstance extends FlatPlayerInstance {
  pause: () => void;
}

const TimeWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export const ReplayableAudioPlayer = React.forwardRef(
  (props: ReplayableAudioPlayerProps, ref: React.Ref<ReplayableAudioRefInstance>) => {
    const {
      url,
      showTime,
      onVolChange,
      vol,
      requireLogin = true,
      changeSpeed = false,
      playbackRate = 1,
      updatePlaybackSpeed = noop,
      ...rest
    } = props;
    const [volume, setVolume] = React.useState(vol || 1);
    const [playing, setPlaying] = React.useState(false);
    const audioRef = React.useRef<FlatPlayerInstance>();
    const [progress, setProgress] = React.useState<undefined | number>();
    React.useImperativeHandle(ref, () => ({
      pause: () => {
        setPlaying(false);
        if (audioRef.current?.pause) {
          audioRef.current.pause();
        }
      },
      ...audioRef.current,
    }));

    const onPlayPause = () => {
      if (audioRef.current) {
        audioRef.current.playPause();
      }
    };

    const onReplay = () => {
      if (audioRef.current) {
        audioRef.current.replay();
      }
    };

    return (
      <Main>
        <div className="audio-controllers">
          <RequiredAuthFake
            title="Để truy cập full tính năng, bạn cần đăng ký tài khoản dolacademy.vn"
            onLoginSuccess={onPlayPause}
            require={requireLogin}
          >
            {({ forceLoginToProceedAction }) => {
              return (
                <Button.Icon
                  type={changeSpeed ? 'thirdparty' : 'secondary'}
                  variant={changeSpeed ? 'normal' : 'dark'}
                  onClick={() => forceLoginToProceedAction(onPlayPause)}
                >
                  {playing ? <IconOtherPause /> : <IconOtherPlay />}
                </Button.Icon>
              );
            }}
          </RequiredAuthFake>

          <RequiredAuthFake
            title="Để truy cập full tính năng, bạn cần đăng ký tài khoản dolacademy.vn"
            onLoginSuccess={onReplay}
            require={requireLogin}
          >
            {({ forceLoginToProceedAction }) => {
              return (
                <Button.Icon
                  disabled={
                    !playing && Math.round(progress) === Math.round(rest.startTimeInSeconds)
                  }
                  type="secondary"
                  variant="dark"
                  onClick={() => forceLoginToProceedAction(onReplay)}
                >
                  <IconArrowsUndo />
                </Button.Icon>
              );
            }}
          </RequiredAuthFake>

          {showTime && (
            <TimeWrapper>
              <Typography.Text variant="semi-bold/10">{`${convertSecondsToMinutedAndSeconds(
                Math.round(progress)
              )} / `}</Typography.Text>
              <Typography.Text variant="semi-bold/10" color="black70">
                {convertSecondsToMinutedAndSeconds(showTime)}
              </Typography.Text>
            </TimeWrapper>
          )}
        </div>
        <FlatAudioPlayer
          onAudioProgress={(sec) => setProgress(sec)}
          className="audio-player-container"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          url={url}
          ref={audioRef}
          interactive
          volume={volume}
          playbackRate={playbackRate}
          {...rest}
        />
        {changeSpeed && (
          <ChangeAudioSpeedComponent
            updatePlaybackSpeed={updatePlaybackSpeed}
            playbackRate={playbackRate}
          />
        )}
        <div className="volume-slider">
          <div className="icon-volume">
            {volume === 0 ? <IconMediaMute /> : <IconMediaVolume />}
          </div>
          <Slider
            className="__audio-slider"
            onChange={(volNum) => {
              setVolume(volNum);
              onVolChange?.(volNum);
            }}
            value={volume}
            min={0}
            max={1}
            step={0.01}
            tooltip={{
              formatter: (v) => Math.ceil(v * 100),
            }}
          />
        </div>
      </Main>
    );
  }
);
